import React, { useState } from 'react';
import { Bars3Icon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { FieldManagerProps } from './types';
import QuickActionMenu from './QuickActionMenu';

const FieldManager: React.FC<FieldManagerProps> = ({
  fieldDefinitions,
  fieldsConfig,
  onFieldConfigChange,
  onReorderFields,
  onQuickAction
}) => {
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Get field icon based on type
  const getFieldIcon = (fieldType: string) => {
    const iconClass = "w-5 h-5";
    
    switch (fieldType) {
      case 'text':
        return (
          <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        );
      case 'email':
        return (
          <svg className={`${iconClass} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'tel':
        return (
          <svg className={`${iconClass} text-purple-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'date':
        return (
          <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'select':
      case 'multiselect':
        return (
          <svg className={`${iconClass} text-yellow-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'textarea':
        return (
          <svg className={`${iconClass} text-teal-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return (
          <svg className={`${iconClass} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  // Get ordered visible fields
  const getOrderedVisibleFields = () => {
    return Object.entries(fieldsConfig)
      .filter(([_, config]) => config.visible)
      .map(([key, config]) => ({
        key,
        config,
        definition: fieldDefinitions[key]
      }))
      .filter(field => field.definition !== undefined)
      .sort((a, b) => a.config.order - b.config.order);
  };

  // Get all fields sorted alphabetically
  const getAllFields = () => {
    return Object.entries(fieldDefinitions)
      .map(([key, definition]) => ({
        key,
        definition,
        config: fieldsConfig[key] || { visible: false, required: false, order: 999 }
      }))
      .sort((a, b) => a.definition.label.localeCompare(b.definition.label));
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, fieldKey: string, index: number) => {
    setDraggedField(fieldKey);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      onReorderFields(draggedIndex, targetIndex);
    }
    
    setDraggedField(null);
    setDraggedIndex(null);
  };

  const visibleFields = getOrderedVisibleFields();
  const allFields = getAllFields();

  return (
    <div className="space-y-8">
      {/* Quick Actions */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Select & Configure Fields</h3>
        <QuickActionMenu
          onEssentialOnly={() => onQuickAction('essential')}
          onShowAll={() => onQuickAction('showAll')}
          onHideAll={() => onQuickAction('hideAll')}
          isEditing={false}
        />
      </div>

      {/* Visible Fields (Draggable) */}
      {visibleFields.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-semibold text-gray-900">
              Selected Fields ({visibleFields.length})
            </span>
            <span className="text-xs text-gray-500">• Drag to reorder</span>
          </div>
          
          <div className="space-y-2">
            {visibleFields.map((field, index) => (
              <div
                key={field.key}
                draggable
                onDragStart={(e) => handleDragStart(e, field.key, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`group flex items-center justify-between p-4 rounded-lg border-2 bg-white border-blue-200 hover:border-blue-400 hover:shadow-md cursor-move transition-all ${
                  draggedField === field.key ? 'opacity-40 scale-95' : ''
                }`}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <Bars3Icon className="h-6 w-6 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                  {getFieldIcon(field.definition.type)}
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-900">
                        {field.definition.label}
                      </span>
                      {field.config.required && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Required
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 capitalize mt-0.5 ml-8">{field.definition.type} field</div>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer select-none" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={(e) => {
                          e.stopPropagation();
                          onFieldConfigChange(field.key, 'visible', false);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-blue-600 rounded-full peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      Visible
                    </span>
                  </label>

                  <label className="flex items-center space-x-2 cursor-pointer select-none" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={field.config.required}
                      onChange={(e) => {
                        e.stopPropagation();
                        onFieldConfigChange(field.key, 'required', e.target.checked);
                      }}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500 transition-colors"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Required
                    </span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Fields (Not Draggable) */}
      {allFields.filter(f => !f.config.visible).length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-semibold text-gray-900">
              Available Fields ({allFields.filter(f => !f.config.visible).length})
            </span>
            <span className="text-xs text-gray-500">• Toggle to add</span>
          </div>
          
          <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
            {allFields
              .filter(f => !f.config.visible)
              .map((field) => (
                <div
                  key={field.key}
                  className="group flex items-center justify-between p-4 rounded-lg border-2 bg-white border-gray-200 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    {getFieldIcon(field.definition.type)}
                    
                    <div className="flex-1">
                      <span className="font-medium text-gray-600 block">
                        {field.definition.label}
                      </span>
                      <div className="text-xs text-gray-500 capitalize mt-0.5">{field.definition.type} field</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2 cursor-pointer select-none">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={(e) => onFieldConfigChange(field.key, 'visible', true)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      </div>
                      <span className="text-sm font-medium text-gray-500">
                        Hidden
                      </span>
                    </label>

                    <div className="w-24 opacity-50">
                      <span className="text-sm text-gray-400">—</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {visibleFields.length === 0 && (
        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-gray-900">No fields selected yet</p>
          <p className="text-sm text-gray-600 mt-1">Turn on the toggle switch to add fields to your form</p>
          <button
            onClick={() => onQuickAction('essential')}
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Add Essential Fields
          </button>
        </div>
      )}
    </div>
  );
};

export default FieldManager;
